import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal, { EASE } from "@/components/Reveal";
import { ArrowLeft, ArrowRight, TrendingUp, Eye, Users, Zap } from "lucide-react";
import { people } from "@/data/people";

const TENDAYI_PHOTO = "https://customer-assets-v7afamib.emergentagent.net/job_ctrlb-preview/artifacts/1ql7ouc2_DP1.png";

const clients = [
  {
    name: "Tendayi M.",
    role: "Founder & Leadership Coach",
    photo: TENDAYI_PHOTO,
    line: "A personal brand built from zero — positioning first, then a content and creative system, run consistently until the audience caught up with the expertise.",
    stats: [
      { icon: TrendingUp, big: "0 → 10K+", label: "Follower growth on Tendayi's profile — from a standing start." },
      { icon: Eye, big: "5M+", label: "Content impressions generated from her content system." },
    ],
  },
  {
    ...people.aarav,
    line: "From posting into the void to a profile that shows up inside sales conversations.",
    stats: [
      { icon: TrendingUp, big: "800 → 6.2K", label: "Followers in six months — no gimmicks, no pods." },
      { icon: Zap, big: "3x", label: "Inbound leads attributed to content." },
    ],
  },
  {
    ...people.meera,
    line: "A carousel system that made her niche stop scrolling — and start subscribing.",
    stats: [
      { icon: Eye, big: "800 → 30K", label: "Average impressions per carousel post." },
      { icon: Users, big: "12 weeks", label: "To a self-sustaining content system." },
    ],
  },
  {
    ...people.tomas,
    line: "Three months of consistency after two years of random posting.",
    stats: [
      { icon: Zap, big: "First", label: "Inbound enterprise lead — straight from a post." },
      { icon: TrendingUp, big: "90 days", label: "To a repeatable presence and pipeline." },
    ],
  },
];

export default function ClientSpotlight() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % clients.length), 6000);
    return () => clearInterval(t);
  }, [paused]);

  const prev = () => setIndex((i) => (i - 1 + clients.length) % clients.length);
  const next = () => setIndex((i) => (i + 1) % clients.length);
  const c = clients[index];

  return (
    <section className="bg-[#09090B] border-t border-[#F3F0E8]/10">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-28 sm:py-36">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#CFE9EF]/70">client spotlights</p>
              <h2
                data-testid="spotlight-heading"
                className="font-serif-d font-black tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-7xl text-[#F3F0E8] mt-6"
              >
                Proof has <span className="italic font-semibold text-[#F3F0E8]/45">faces.</span>
              </h2>
            </div>
            <span data-testid="spotlight-counter" className="text-xs tracking-[0.3em] text-[#118C4F] pb-2">
              0{index + 1} <span className="text-[#F3F0E8]/30">/ 0{clients.length}</span>
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div
            data-testid="spotlight-showcase"
            className="mt-14 border-y border-[#F3F0E8]/10 py-14 sm:py-20"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                data-testid="spotlight-slide"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.55, ease: EASE }}
                className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center"
              >
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-5">
                    <img
                      src={c.photo}
                      alt={`${c.name} — client portrait`}
                      data-testid="spotlight-photo"
                      loading="lazy"
                      className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-[#118C4F]/40"
                    />
                    <div>
                      <h3 data-testid="spotlight-name" className="font-serif-d font-black tracking-tight text-3xl sm:text-4xl text-[#F3F0E8]">
                        {c.name}
                      </h3>
                      <p className="text-sm text-[#F3F0E8]/50 mt-1">{c.role}</p>
                    </div>
                  </div>
                  <p className="text-base sm:text-lg text-[#F3F0E8]/60 leading-relaxed mt-8 max-w-md">
                    {c.line}
                  </p>
                </div>

                <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[#F3F0E8]/10 border border-[#F3F0E8]/10">
                  {c.stats.map((s, si) => (
                    <div
                      key={si}
                      data-testid={`spotlight-stat-${si + 1}`}
                      className="group bg-[#09090B] p-8 sm:p-10 hover:bg-[#F3F0E8]/[0.03] transition-colors duration-500"
                    >
                      <s.icon size={20} className="text-[#118C4F]" strokeWidth={1.8} aria-hidden="true" />
                      <p className="font-serif-d font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#F3F0E8] mt-5">
                        {s.big}
                      </p>
                      <p className="text-xs sm:text-sm text-[#F3F0E8]/55 leading-relaxed mt-3">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-between mt-12">
              <div className="flex gap-2 flex-1 mr-8" aria-hidden="true">
                {clients.map((_, i) => (
                  <span
                    key={i}
                    className={`h-px flex-1 transition-colors duration-500 ${i === index ? "bg-[#118C4F]" : "bg-[#F3F0E8]/15"}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  data-testid="spotlight-prev"
                  aria-label="Previous client spotlight"
                  onClick={prev}
                  className="w-11 h-11 border border-[#F3F0E8]/20 hover:border-[#118C4F] hover:text-[#118C4F] text-[#F3F0E8]/70 flex items-center justify-center transition-colors duration-300"
                >
                  <ArrowLeft size={16} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  data-testid="spotlight-next"
                  aria-label="Next client spotlight"
                  onClick={next}
                  className="w-11 h-11 border border-[#F3F0E8]/20 hover:border-[#118C4F] hover:text-[#118C4F] text-[#F3F0E8]/70 flex items-center justify-center transition-colors duration-300"
                >
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        <p data-testid="spotlight-note" className="text-[10px] uppercase tracking-[0.25em] text-[#F3F0E8]/25 mt-8">
          Spotlights 02–04 are sample layouts pending full case studies
        </p>
      </div>
    </section>
  );
}
